// One packed 0xRRGGBB ink, split three ways.
//
// Every canvas, shader and stylesheet of the client reads a colour as one
// number, and each one wants it in another form: a 2D canvas takes a CSS
// string, a `vec3` uniform takes three fractions, and a custom property
// takes three channels. This leaf holds the three splits, so no module
// spells the shifts again. It imports nothing, so the portrait worker and
// every page can load it, and no split allocates more than its answer. The
// one mix of two inks, and the two readers of a palette token, live here as
// well, for the same reason.

/** The three channels of a packed ink, 0..255 each. */
export function channels(color: number): [number, number, number] {
  return [(color >> 16) & 0xff, (color >> 8) & 0xff, color & 0xff];
}

/** The three sRGB fractions of a packed ink, 0..1 each, the form a `vec3`
 *  uniform wants. */
export function rgb(color: number): [number, number, number] {
  return [((color >> 16) & 255) / 255, ((color >> 8) & 255) / 255, (color & 255) / 255];
}

/** `#rrggbb` of a packed ink, the form an SVG attribute and a custom
 *  property take. The ink is not masked: an ink above 0xffffff writes more
 *  than six digits. */
export function hex(color: number): string {
  return '#' + color.toString(16).padStart(6, '0');
}

/** `rgba()` of a packed ink, so a paint never touches `globalAlpha`. */
export function rgba(color: number, alpha: number): string {
  const r = (color >> 16) & 0xff, g = (color >> 8) & 0xff, b = color & 0xff;
  return `rgba(${r},${g},${b},${alpha})`;
}

/**
 * `a` moved `k` of the way to `b`, channel by channel: each channel is
 * rounded and held to 0..255, so `k` of 0 is `a` and `k` of 1 is `b`. It
 * allocates nothing and makes no closure, so a frame path may call it.
 */
export function mixColor(a: number, b: number, k: number): number {
  return (mixChannel(a, b, 16, k) << 16) | (mixChannel(a, b, 8, k) << 8) | mixChannel(a, b, 0, k);
}

/** `'#d8c66a'` (or `'d8c66a'`) as one packed ink. */
export function packHex(s: string): number {
  return Number.parseInt(s.replace('#', ''), 16) & 0xffffff;
}

/** A palette `-rgb` token such as `'2 11 18'` as one packed ink. A missing
 *  channel reads 0. */
export function packChannels(s: string): number {
  const p = s.trim().split(/\s+/);
  return ((Number(p[0] ?? 0) & 0xff) << 16) | ((Number(p[1] ?? 0) & 0xff) << 8)
    | (Number(p[2] ?? 0) & 0xff);
}

/** One channel of `mixColor`, at `shift`. */
function mixChannel(a: number, b: number, shift: number, k: number): number {
  const x = (a >> shift) & 0xff, y = (b >> shift) & 0xff;
  return Math.max(0, Math.min(255, Math.round(x + (y - x) * k)));
}
